<template>
  <fieldset>
    <legend class="form__label pb-8">Birth date</legend>
    <HelperText custom-classes="-mt-4 mb-16 -ml-4" text="This information will not be visible on your profile" />
    <div class="-mt-4 inline-flex">
      <poppins-text-field
        id="month"
        v-model="form.month"
        label-name="MM"
        name="dob-month"
        type="text"
        maxlength="2"
        pattern="[0-9]*"
        class="mb-24 mr-18 w-5.5rem"
        inputmode="numeric"
        aria-label="Birth month"
        required
        :status="formStatuses.month"
        :limit-input-to-pattern="true"
        @blur="validateMonth"
      />
      <poppins-text-field
        id="day"
        v-model="form.day"
        label-name="DD"
        name="dob-day"
        type="text"
        maxlength="2"
        pattern="[0-9]*"
        class="mb-24 mr-18 w-5.5rem"
        inputmode="numeric"
        aria-label="Birth date"
        required
        :status="formStatuses.day"
        :limit-input-to-pattern="true"
        @blur="validateDay"
      />
      <poppins-text-field
        id="year"
        v-model="form.year"
        label-name="YYYY"
        name="dob-year"
        type="text"
        maxlength="4"
        pattern="[0-9]*"
        class="mb-24 w-6.5rem"
        inputmode="numeric"
        aria-label="Birth year"
        required
        :status="formStatuses.year"
        :limit-input-to-pattern="true"
        @blur="validateYear"
      />
    </div>
    <div v-if="formStatusesErrors" class="-mt-16 mb-16">
      <div v-for="key in formStatusesErrors" :key="key" class="flex relative">
        <HelperText :id="key" :status="0" :text="helperText(0, key)" />
      </div>
    </div>
  </fieldset>
</template>

<script lang="ts">
import { formatISO, isValid } from 'date-fns';
import helperStatus from '@/helpers/status';
import TextField from '@/components/textfield/TextField.vue';
import HelperText from '@/components/helper_text/HelperText.vue';

export default {
  name: 'BirthDateFields',
  components: {
    PoppinsTextField: TextField,
    HelperText,
  },
  emits: ['input'],
  data: () => ({
    form: {
      month: '',
      day: '',
      year: '',
    },
    formStatuses: {
      month: helperStatus.Neutral,
      day: helperStatus.Neutral,
      year: helperStatus.Neutral,
    },
  }),
  computed: {
    currentYear(): number {
      return new Date().getFullYear();
    },
    daysInSelectedMonth(): number {
      if (this.formattedMonth) {
        // if the user did not put a year in, default to a leap year for February
        // to avoid rejecting valid leap year dates (e.g., 2/29/2000)
        // For other months, year doesn't affect the number of days
        const year: number = this.formattedYear || (this.formattedMonth === 2 ? 2000 : this.currentYear);
        // months in js are zero indexed, get the last day of the selected month
        // by using day 0 of the next month (formattedMonth is 1-indexed)
        return new Date(year, this.formattedMonth, 0).getDate();
      }
      return 31;
    },
    formStatusesErrors(): Array<string> {
      return Object.keys(this.formStatuses).filter((key) => this.formStatuses[key] === helperStatus.Error);
    },
    formattedMonth(): number | null {
      return this.form.month ? parseInt(this.form.month, 10) : null;
    },
    formattedDay(): number | null {
      return this.form.day ? parseInt(this.form.day, 10) : null;
    },
    formattedYear(): number | null {
      return this.form.year ? parseInt(this.form.year, 10) : null;
    },
    monthIsValid(): boolean | null {
      return this.formattedMonth ? this.formattedMonth >= 1 && this.formattedMonth <= 12 : false;
    },
    dayIsValid(): boolean | null {
      return this.formattedDay ? this.formattedDay >= 1 && this.formattedDay <= this.daysInSelectedMonth : false;
    },
    yearIsValid(): boolean | null {
      return this.formattedYear ? this.formattedYear >= this.currentYear - 100 && this.formattedYear <= this.currentYear : false;
    },
    parsedDOB(): Date | null {
      // if month was not parsed, we can't do math on it below
      if (Number.isNaN(this.formattedMonth)) return null;
      if (Number.isNaN(this.formattedYear)) return null;
      if (Number.isNaN(this.formattedDay)) return null;

      let parsedDate;

      if (this.formattedMonth && this.formattedDay && this.formattedYear) {
        // months in js are zero indexed, remove one from the month to create our date.
        parsedDate = new Date(this.formattedYear, this.formattedMonth - 1, this.formattedDay);
      }

      return isValid(parsedDate) ? parsedDate : null;
    },
    formattedDOB(): string | null {
      if (this.parsedDOB) {
        // use dateFNS to correctly format DOB as a final validation that this date exists
        return formatISO(this.parsedDOB, { representation: 'date' });
      }

      return null;
    },
  },
  watch: {
    'form.month': function onMonthChange() {
      this.onInput();
      this.formStatuses.month = helperStatus.Neutral;
      // Re-validate day when month changes, as days in month depend on the month
      if (this.form.day) {
        this.formStatuses.day = helperStatus.Neutral;
        this.validateDay();
      }
    },
    'form.day': function onDayChange() {
      this.onInput();
      this.formStatuses.day = helperStatus.Neutral;
    },
    'form.year': function onYearChange() {
      this.onInput();
      this.formStatuses.year = helperStatus.Neutral;
    },
  },
  methods: {
    onInput() {
      this.$emit('input', 'birthDate', this.formattedDOB);
    },
    validateAll() {
      this.validateMonth();
      this.validateDay();
      this.validateYear();

      return this.formStatusesErrors.length === 0;
    },
    validateMonth() {
      if (!this.monthIsValid) {
        this.formStatuses.month = helperStatus.Error;
      }
    },
    validateDay() {
      if (!this.dayIsValid) {
        this.formStatuses.day = helperStatus.Error;
      } else {
        this.formStatuses.day = helperStatus.Neutral;
      }
    },
    validateYear() {
      if (!this.yearIsValid) {
        this.formStatuses.year = helperStatus.Error;
      }
      // Re-validate day when year changes, as days in month depend on the year (e.g., February 29 in leap years)
      if (this.form.day) {
        this.validateDay();
      }
    },
    helperText(status: helperStatus, type: string) {
      if (status === helperStatus.Error) {
        return `Enter a valid ${type}`;
      }
      return '';
    },
  },
};
</script>
